import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Card, CardBody, Col } from 'reactstrap';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import SmallLoader from '../../../../shared/components/SmallLoader';
import { setCurrencyFormat } from '../../../../utils/helpers';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const colorData = Obj =>
  Obj &&
  Obj.length > 0 &&
  Obj.map((element) => {
    const max = Math.max(...Obj);
    const opecity = Math.floor(element) / max;
    const color = `rgba(0, 123, 255, ${opecity})`;
    return color;
  });

const color = '#007bff';
const addMonthlyAmount = (total, num) => Number(total) + Number(num);
const reduceArray = (obj) => {
  if (obj && obj.length > 0) {
    return obj.reduce(addMonthlyAmount);
  }
  return 0;
};

const month = ['12', '11', '10', '9', '8', '7', '6', '5', '4', '3', '2', '1'];
const monthString = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const getChartData = (Obj) => {
  const labels = [];
  const values = [];
  const yearArray = [];
  if (Obj && Obj.length > 0) {
    Obj.forEach((innerObj) => {
      if (!yearArray.includes(innerObj.salesYear)) {
        yearArray.push(innerObj.salesYear);
      }
    });
    yearArray
      .sort((a, b) => b - a)
      .forEach((ele) => {
        month.forEach((monthObj) => {
          if (
            reduceArray(
              Obj.filter(x => x.salesYear === ele)
                .filter(data => data.salesMonth === monthObj)
                .map(x => x.orderCount),
            )
          ) {
            labels.push(`${monthString[monthObj - 1]}:${ele}`);
            values.push(
              reduceArray(
                Obj.filter(x => x.salesYear === ele)
                  .filter(data => data.salesMonth === monthObj)
                  .map(x => x.orderCount),
              ),
            );
          }
        });
      });
  }
  return { label: labels, data: values };
};

const getState = data => ({
  labels: getChartData(data).label,
  datasets: [
    {
      label: '',
      borderColor: color,
      borderWidth: 0,
      hoverBackgroundColor: color,
      hoverBorderColor: color,
      data: getChartData(data).data,
      backgroundColor: getChartData(data).data ? colorData(getChartData(data).data) : '#000',
      datalabels: {
        align: 'end',
        anchor: 'end',
        fontSize: 7,
        font: {
          weight: 500,
          size: 10,
        },
        formatter: value => `${setCurrencyFormat(Number(value).toFixed(0) || '0.00', false)}`,
      },
    },
  ],
});

const options = {
  indexAxis: 'y', // This makes the bar chart horizontal
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    tooltip: {
      callbacks: {
        label: (context) => {
          let label = context.dataset.label || '';
          if (label) {
            label += ': ';
          }
          label += setCurrencyFormat(Number(context.parsed.x).toFixed(0), false);
          return label;
        },
      },
    },
    legend: {
      display: false,
    },
  },
  layout: {
    padding: {
      // top: 24,
      // right: 16,
      // bottom: 0,
      // left: 8,
    },
  },
  elements: {
    bar: {
      borderWidth: 0,
    },
  },
  scales: {
    x: {
      stacked: false,
    },
    y: {
      stacked: false,
    },
  },
};

const HorizontalBars = (props) => {
  const { title, revenueData, setModel, isLoading } = props;
  const { t } = useTranslation('common');
  const [data, setData] = useState(getState([])); // Initialize with empty array
  const [currentYearTotalAmount, setCurrentYearTotalAmount] = useState(0);
  const [previousYearTotalAmount, setPreviousYearTotalAmount] = useState(0);

  useEffect(() => {
    setData(getState(revenueData || [])); // Add null check
    if (revenueData && revenueData.length > 0) {
      setCurrentYearTotalAmount(
        revenueData
          .filter(
            item =>
              Number(item.salesYear) === new Date().getFullYear() &&
              Number(item.salesMonth) === new Date().getMonth() + 1,
          )
          .map(item => item.amount)
          .reduce((a, b) => Number(a) + Number(b), 0)
          .toFixed(2),
      );
      setPreviousYearTotalAmount(
        revenueData
          .filter(
            item =>
              Number(item.salesYear) === new Date().getFullYear() - 1 &&
              Number(item.salesMonth) === new Date().getMonth(),
          )
          .map(item => item.amount)
          .reduce((a, b) => Number(a) + Number(b), 0)
          .toFixed(2),
      );
    }
  }, [revenueData]);

  // Add null check for revenueData
  const totalOrder = reduceArray((revenueData || []).map(x => x.orderCount));
  
  const getPercentage = (currValue, preValue) =>
    (((currValue - preValue) / preValue) * 100).toFixed(2) || '0.00';

  return (
    <Col md={12} lg={12} xl={12}>
      <Card>
        <CardBody className="card-shadow">
          {isLoading ? (
            <>
              <div className="card__title">
                <h5 className="bold-text">{t(title)}</h5>
              </div>
              <div
                style={{ minHeight: '200px' }}
                className="row align-items-center justify-content-center"
              >
                <SmallLoader loading={isLoading} />
              </div>
            </>
          ) : (
            <>
              <div className="card__title">
                <h5 className="bold-text">{t(title)}</h5>
                <button
                  id='btnHorViewReport'
                  type="button"
                  className="view-report btn btn-primary btn-sm"
                  onClick={() => setModel(true)}
                >
                  View Report
                </button>
                <div className="second-header-for-chart_forHorizontal">
                  <div className="left">
                    <h5 className="bold-text colored">{totalOrder}</h5>
                    <h5 className="bold-text">{t('Total Orders')}</h5>
                  </div>
                  <div className="right">
                    {getPercentage(currentYearTotalAmount, previousYearTotalAmount) < 0 && (
                      <h5 className="bold-text colored">
                        <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                          <path
                            d="M16,18L18.29,15.71L13.41,10.83L9.41,14.83L2,7.41L3.41,6L9.41,12L13.41,8L19.71,
                            14.29L22,12V18H16Z"
                          />
                        </svg>
                        {getPercentage(currentYearTotalAmount, previousYearTotalAmount)}%
                      </h5>
                    )}
                    {getPercentage(currentYearTotalAmount, previousYearTotalAmount) > 0 && (
                      <h5 className="bold-text colored">
                        <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                          <path
                            d="M16,6L18.29,8.29L13.41,13.17L9.41,9.17L2,16.59L3.41,18L9.41,
                              12L13.41,16L19.71,9.71L22,12V6H16Z"
                          />
                        </svg>
                        {getPercentage(currentYearTotalAmount, previousYearTotalAmount)}%
                      </h5>
                    )}
                    <h5 className="bold-text">{t('Since last month')}</h5>
                  </div>
                </div>
              </div>
              <div>
                <Bar height={1000} data={data || []} options={options} />
              </div>
            </>
          )}
        </CardBody>
      </Card>
    </Col>
  );
};

HorizontalBars.propTypes = {
  title: PropTypes.string.isRequired,
  revenueData: PropTypes.array.isRequired, // Changed from string to array
  setModel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
};

export default HorizontalBars;